import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiX, FiFileText, FiTrash2, FiAlertTriangle } from 'react-icons/fi';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

/**
 * Pick a template to start a letter from.
 *
 * Applying one replaces the title, details block, body and closing on the
 * current draft — so if the draft already has content, the confirm makes that
 * loss explicit rather than silently discarding work.
 */
function LetterTemplatePicker({ onPick, onClose, hasContent }) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const token = localStorage.getItem('authToken');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/letter-templates`, config);
        setTemplates(res.data || []);
      } catch (err) {
        console.error('Could not load templates', err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = async (tpl) => {
    if (hasContent && !window.confirm(
      `Start from "${tpl.name}"?\n\nThis replaces the title, details block and body of the letter you are editing.`
    )) return;
    setBusyId(tpl.id);
    try {
      // The list omits bodies; fetch the full template before applying it.
      const res = await axios.get(`${API_URL}/letter-templates/${tpl.id}`, config);
      onPick(res.data);
    } catch (err) {
      alert('Could not load that template: ' + (err.response?.data?.error || err.message));
      setBusyId(null);
    }
  };

  const remove = async (tpl, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the template "${tpl.name}"?\n\nLetters already written from it are unaffected.`)) return;
    try {
      await axios.delete(`${API_URL}/letter-templates/${tpl.id}`, config);
      setTemplates(prev => prev.filter(t => t.id !== tpl.id));
    } catch (err) {
      alert('Could not delete: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 2500,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
    }}>
      <div style={{
        background: 'white', borderRadius: '12px', width: '100%', maxWidth: '640px',
        maxHeight: '86vh', overflow: 'auto', boxShadow: '0 18px 50px rgba(0,0,0,.35)',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '15px 18px', borderBottom: '1px solid #e5e7eb',
        }}>
          <strong style={{ fontSize: '15px', color: '#111827' }}>Start from a template</strong>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', lineHeight: 0 }}>
            <FiX size={20} />
          </button>
        </div>

        {hasContent && (
          <div style={{
            display: 'flex', gap: '8px', alignItems: 'flex-start',
            margin: '14px 18px 0', padding: '10px 12px', background: '#fffbeb',
            border: '1px solid #fde68a', borderRadius: '8px', fontSize: '13px', color: '#92400e',
          }}>
            <FiAlertTriangle size={15} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>This letter already has content. Applying a template replaces the title, details block and body.</span>
          </div>
        )}

        <div style={{ padding: '14px 18px 18px' }}>
          {loading ? (
            <div style={{ color: '#9ca3af', fontSize: '13px', padding: '16px 0' }}>Loading...</div>
          ) : !templates.length ? (
            <div style={{
              padding: '26px', textAlign: 'center', color: '#9ca3af', fontSize: '13px',
              border: '1px dashed #d1d5db', borderRadius: '8px',
            }}>
              <FiFileText size={20} style={{ marginBottom: '6px' }} />
              <div>No templates yet. Build a letter you like, then use &ldquo;Save as Template&rdquo;.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {templates.map(tpl => (
                <div
                  key={tpl.id}
                  onClick={() => busyId === null && choose(tpl)}
                  style={{
                    border: '1px solid #e5e7eb', borderRadius: '9px', padding: '12px 14px',
                    cursor: busyId === null ? 'pointer' : 'wait',
                    opacity: busyId !== null && busyId !== tpl.id ? 0.5 : 1,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0f2942'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: '7px' }}>
                      {tpl.name}
                      {tpl.is_builtin && (
                        <span style={{
                          fontSize: '10.5px', fontWeight: 700, padding: '1px 7px', borderRadius: '999px',
                          background: '#eef2ff', color: '#4338ca',
                        }}>
                          BUILT-IN
                        </span>
                      )}
                    </div>
                    {tpl.description && (
                      <div style={{ fontSize: '12.5px', color: '#6b7280', marginTop: '3px', lineHeight: 1.45 }}>
                        {tpl.description}
                      </div>
                    )}
                  </div>
                  {user?.can_manage_payroll && (
                    <button
                      onClick={(e) => remove(tpl, e)}
                      title="Delete template"
                      style={{
                        padding: '6px 8px', background: 'white', color: '#dc2626',
                        border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer',
                        lineHeight: 0, flexShrink: 0,
                      }}
                    >
                      <FiTrash2 size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LetterTemplatePicker;
